import { useContext, useMemo } from "react";
import { Link } from "react-router-dom";

import { Loading } from "@/shared/components/Loading/Loading";
import { ClientAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { TestAnswerStatusEnum } from "@/shared/services/tests/tests.types";
import cn from "classnames";
import { ITest } from "@/modules/assessments/types/test.interface";
import { useReduxClientsHook } from "@/modules/client/hooks/useReduxClientsHook";
import TableTests, { ITestTable } from "@/modules/tables/TableTests";
import { SEO } from "@/ui-components/commonComponents/SEO/SEO";

import { NEW_ROUTE, TEST_NORMS_ROUTE } from "@/routing/AppRouter/routes.constants";

import { useGetAllTestsHook } from "../hooks/useGetAllTests.hook";
import { useGetTestsStatsHook } from "../hooks/useGetTestsStats.hook";

import style from "./style.module.css";
import buttonStyle from "@/ui-components/commonComponents/Button/style.module.css";

export default function Tests() {
  const { tests, isLoading } = useGetAllTestsHook();
  const { testsStats, isLoading: isLoadingStats } = useGetTestsStatsHook();

  const { reduxClients } = useReduxClientsHook();

  const clientAbility = useContext(ClientAbilityContext);
  const canCreateTest = clientAbility.can(Action.Create, Subject.TEST);
  const canCreateNorms = clientAbility.can(Action.Create, Subject.NORM);

  const dataList = useMemo<ITestTable[]>(() => {
    return tests.map((test: ITest) => {
      const clientObject = reduxClients.find((client) => client.clientId === test.clientId);

      if (!testsStats) {
        return {
          key: test.testId,
          id: test.testId,
          name: test.name,
          internalName: test.internalName,
          client: clientObject?.name,
          questionsCount: 0,
          joinedAssessments: 0,
          finishedTestQuantity: 0,
          countOfNorms: 0,
          suggestedTime: 0,
          startsSections: [],
        };
      }

      return {
        key: test.testId,
        id: test.testId,
        name: test.name,
        internalName: test.internalName,
        questionsCount: testsStats.testsElements[test.testId]
          ? testsStats.testsElements[test.testId].countOfElements
          : 0,
        client: clientObject?.name,
        joinedAssessments: testsStats.assessmentTests[test.testId]
          ? testsStats.assessmentTests[test.testId].countOfAssessments
          : 0,
        finishedTestQuantity:
          testsStats.testsAnswers[test.testId] && testsStats.testsAnswers[test.testId][TestAnswerStatusEnum.Done]
            ? testsStats.testsAnswers[test.testId][TestAnswerStatusEnum.Done].countOfAnswers
            : 0,
        countOfNorms: testsStats.testsNorms[test.testId] ? testsStats.testsNorms[test.testId].countOfNorms : 0,
        suggestedTime: testsStats.testsTime[test.testId] ? testsStats.testsTime[test.testId].suggestedTime : 0,
        startsSections: testsStats.testsSections[test.testId] ?? [],
      };
    });
  }, [tests, testsStats, reduxClients]);

  if (isLoading || isLoadingStats) {
    return <Loading />;
  }

  return (
    <div className="main-container">
      <SEO titleParams={["Tests"]} />
      <div className="inner-container">
        <TableTests
          dataList={dataList}
          headerActions={
            <div className={style.tableHeaderLinkContainer}>
              {canCreateTest && (
                <Link to={NEW_ROUTE} className={cn(buttonStyle.linkAsButton, buttonStyle.linkAsButtonPrimary)}>
                  New Test
                </Link>
              )}
              {canCreateNorms && (
                <Link
                  to={`${TEST_NORMS_ROUTE}/${NEW_ROUTE}`}
                  role="button"
                  className={cn(buttonStyle.linkAsButton, buttonStyle.linkAsButtonOutlined)}
                >
                  New Norms
                </Link>
              )}
            </div>
          }
        />
      </div>
    </div>
  );
}
